/**
 * AutoCount — Inventory Dashboard
 * The product landing screen: live warehouse KPIs, stock movement,
 * reorder alerts (driven by each part's reorder point), a low-stock
 * watchlist and recent shipments, all driven by seeded/live data.
 */

import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import DataTable from "examples/Tables/DataTable";

import { listInventory, listShipments, InventoryItem, ShipmentSummary } from "api";
import { useAuth } from "context/auth/authContext";

const STATUS_COLORS: Record<string, "success" | "info" | "warning" | "error" | "secondary"> = {
  COMPLETED: "success",
  IN_TRANSIT: "info",
  RECEIVING: "warning",
  PENDING: "secondary",
  DISCREPANCY: "error",
  CANCELLED: "error",
};

/** Margin display when both cost and retail price are present, else "—". */
function marginLabel(item: InventoryItem): { text: string; pct: number | null } {
  if (item.costPrice == null || item.retailPrice == null || item.retailPrice <= 0) {
    return { text: "—", pct: null };
  }
  const dollars = item.retailPrice - item.costPrice;
  const pct = Math.round((dollars / item.retailPrice) * 100);
  return { text: `$${dollars.toFixed(2)} (${pct}%)`, pct };
}

function AutoCountDashboard(): JSX.Element {
  const { authState } = useAuth();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [shipments, setShipments] = useState<ShipmentSummary[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listInventory({ pageSize: 200 }), listShipments({ pageSize: 50 })])
      .then(([inv, ship]) => {
        if (cancelled) return;
        setItems(inv.items);
        setShipments(ship.data);
      })
      .catch(() => {
        /* seeded demo — keep the shell rendered on transient errors */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stockedSkus = items.length;
  const totalUnits = useMemo(
    () => items.reduce((sum, i) => sum + i.quantityOnHand, 0),
    [items]
  );
  const variances = useMemo(
    () => items.filter((i) => i.variance !== 0).length,
    [items]
  );

  // Reorder alerts — driven by each part's reorder point (on hand <= reorder point).
  const reorderAlerts = useMemo(
    () =>
      items
        .filter(
          (i) =>
            i.reorderPoint != null && i.quantityOnHand <= (i.reorderPoint as number)
        )
        .sort(
          (a, b) =>
            a.quantityOnHand - (a.reorderPoint as number) -
            (b.quantityOnHand - (b.reorderPoint as number))
        ),
    [items]
  );

  const lowStock = useMemo(
    () =>
      [...items]
        .filter((i) => i.quantityAvailable <= 15)
        .sort((a, b) => a.quantityAvailable - b.quantityAvailable),
    [items]
  );

  const topStock = useMemo(
    () => [...items].sort((a, b) => b.quantityOnHand - a.quantityOnHand).slice(0, 6),
    [items]
  );

  const chart = useMemo(
    () => ({
      labels: topStock.map((i) => i.partNumber),
      datasets: { label: "Units on hand", data: topStock.map((i) => i.quantityOnHand) },
    }),
    [topStock]
  );

  const warehouseName = authState.user?.defaultWarehouse?.name || "Main Warehouse";

  const reorderRows = reorderAlerts.slice(0, 6).map((i) => {
    const m = marginLabel(i);
    return {
      part: (
        <MDTypography variant="button" fontWeight="medium">
          {i.partNumber}
        </MDTypography>
      ),
      name: i.partName,
      onhand: (
        <MDTypography
          variant="button"
          fontWeight="bold"
          color={i.quantityOnHand === 0 ? "error" : "warning"}
        >
          {i.quantityOnHand}
        </MDTypography>
      ),
      reorder: i.reorderPoint ?? "—",
      supplier: i.supplierName || "—",
      margin: (
        <MDTypography
          variant="button"
          fontWeight="medium"
          color={m.pct != null && m.pct < 0 ? "error" : "text"}
        >
          {m.text}
        </MDTypography>
      ),
    };
  });

  const lowStockRows = lowStock.slice(0, 6).map((i) => ({
    part: (
      <MDTypography variant="button" fontWeight="medium">
        {i.partNumber}
      </MDTypography>
    ),
    name: i.partName,
    available: (
      <MDTypography
        variant="button"
        fontWeight="bold"
        color={i.quantityAvailable === 0 ? "error" : i.quantityAvailable <= 7 ? "warning" : "success"}
      >
        {i.quantityAvailable}
      </MDTypography>
    ),
    bin: i.binLocation || i.location || "—",
  }));

  const shipmentRows = shipments.slice(0, 5).map((s) => ({
    shipment: (
      <MDTypography variant="button" fontWeight="medium">
        {s.shipmentNumber}
      </MDTypography>
    ),
    supplier: s.supplierName || "—",
    progress: `${s.receivedItems} / ${s.totalItems}`,
    status: (
      <Chip
        size="small"
        label={s.status.replace("_", " ")}
        color={STATUS_COLORS[s.status] || "secondary"}
        variant="outlined"
        sx={{ textTransform: "capitalize", fontWeight: 600 }}
      />
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={1}>
          <MDTypography variant="h4" fontWeight="bold">
            Inventory Dashboard
          </MDTypography>
          <MDTypography variant="button" color="text" fontWeight="regular">
            Live stock position · {warehouseName}
          </MDTypography>
        </MDBox>

        <Grid container spacing={3} mt={0}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="inventory_2"
                title="Stocked SKUs"
                count={stockedSkus}
                percentage={{ color: "info", amount: warehouseName, label: "" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="widgets"
                title="Units On Hand"
                count={totalUnits.toLocaleString()}
                percentage={{ color: "success", amount: "", label: "across all bins" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="error"
                icon="notifications_active"
                title="Reorder Needed"
                count={reorderAlerts.length}
                percentage={{ color: "error", amount: "", label: "at/below reorder point" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="warning"
                icon="rule"
                title="Count Variances"
                count={variances}
                percentage={{ color: "warning", amount: "", label: "need review" }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* Reorder alerts — driven by each part's reorder point */}
        <Grid container spacing={3} mt={0}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="error"
                borderRadius="lg"
                coloredShadow="error"
              >
                <MDTypography variant="h6" color="white">
                  Reorder alerts {reorderAlerts.length > 0 && `(${reorderAlerts.length})`}
                </MDTypography>
                <MDTypography variant="caption" color="white" sx={{ opacity: 0.9 }}>
                  Parts at or below their reorder point
                </MDTypography>
              </MDBox>
              <MDBox pt={1}>
                <DataTable
                  table={{
                    columns: [
                      { Header: "Part", accessor: "part", width: "16%" },
                      { Header: "Name", accessor: "name", width: "32%" },
                      { Header: "On hand", accessor: "onhand", width: "12%" },
                      { Header: "Reorder pt", accessor: "reorder", width: "12%" },
                      { Header: "Supplier", accessor: "supplier", width: "16%" },
                      { Header: "Margin", accessor: "margin", width: "12%" },
                    ],
                    rows: reorderRows,
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={0}>
          <Grid item xs={12} lg={5}>
            <MDBox mb={3}>
              <ReportsBarChart
                color="info"
                title="Top stock by SKU"
                description="Units on hand for your best-stocked parts"
                date="updated just now"
                chart={chart}
              />
            </MDBox>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Card sx={{ height: "100%" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="warning"
                borderRadius="lg"
                coloredShadow="warning"
              >
                <MDTypography variant="h6" color="white">
                  Low-stock watchlist
                </MDTypography>
              </MDBox>
              <MDBox pt={1}>
                <DataTable
                  table={{
                    columns: [
                      { Header: "Part", accessor: "part", width: "20%" },
                      { Header: "Name", accessor: "name", width: "45%" },
                      { Header: "Available", accessor: "available", width: "18%" },
                      { Header: "Bin", accessor: "bin", width: "17%" },
                    ],
                    rows: lowStockRows,
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={0}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Recent shipments
                </MDTypography>
              </MDBox>
              <MDBox pt={1}>
                <DataTable
                  table={{
                    columns: [
                      { Header: "Shipment", accessor: "shipment", width: "20%" },
                      { Header: "Supplier", accessor: "supplier", width: "30%" },
                      { Header: "Received", accessor: "progress", width: "25%" },
                      { Header: "Status", accessor: "status", width: "25%" },
                    ],
                    rows: shipmentRows,
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AutoCountDashboard;
